import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import * as Sentry from '@sentry/react';

/**
 * Log error to Sentry in production, console in development.
 * This is the single entry point for all error reporting.
 */
export const logError = (error: Error, errorInfo?: { componentStack?: string }) => {
	if (import.meta.env.DEV) {
		console.error(`[ErrorBoundary] ${error.name}: ${error.message}`);
		if (error.stack) {
			console.error(error.stack);
		}
		if (errorInfo?.componentStack) {
			console.error('Component stack:', errorInfo.componentStack);
		}
		return;
	}

	Sentry.captureException(error, {
		contexts: {
			react: errorInfo?.componentStack ? { componentStack: errorInfo.componentStack } : undefined,
		},
		extra: {
			url: window.location.href,
		},
	});
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: Error): boolean => {
	return error.message.includes('fetch') || error.message.includes('Network') || error.message.includes('ERR_NETWORK');
};

/**
 * Check if error is a chunk loading error (common after deploys in SPAs)
 */
export const isChunkLoadError = (error: Error): boolean => {
	return (
		error.message.includes('Loading chunk') ||
		error.message.includes('ChunkLoadError') ||
		error.message.includes('Failed to fetch dynamically imported module')
	);
};

/**
 * Get user-friendly error message based on error type
 */
export const getUserFriendlyErrorMessage = (error: Error): string => {
	if (isNetworkError(error)) {
		return i18n._(msg`Please check your internet connection and try again.`);
	}

	if (isChunkLoadError(error)) {
		return i18n._(msg`The application has been updated. Please refresh the page.`);
	}

	return i18n._(msg`Something went wrong. Please try again or contact support if the problem persists.`);
};
